import React from 'react';
import SummarySectionCard from './SummarySectionCard';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface SpeedSummaryCardProps {
  speechRateAlerts: SpeechRateAlert[];
  speechRateAssessment?: string;
  onViewInTimeline?: () => void;
}

const SpeedSummaryCard: React.FC<SpeedSummaryCardProps> = ({
  speechRateAlerts,
  speechRateAssessment,
  onViewInTimeline,
}) => {
  const alertCount = speechRateAlerts?.length ?? 0;

  const paceLabel = alertCount === 0 ? 'Good Pace' : alertCount === 1 ? 'Watch Pace' : 'Too Fast';
  const paceColor =
    alertCount === 0
      ? 'bg-success-tint text-success border border-success'
      : alertCount === 1
      ? 'bg-warning-tint text-warning border border-warning'
      : 'bg-error-tint text-error border border-error';

  const statusBadge = (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${paceColor}`}>{paceLabel}</span>
  );

  const avgWpm =
    alertCount > 0
      ? Math.round(
          speechRateAlerts.reduce((sum, a) => sum + (a.words_per_minute ?? 0), 0) / alertCount
        )
      : null;

  const coachingItems: string[] =
    alertCount > 1
      ? [
          `The salesperson was speaking too fast in ${alertCount} moments during the call. Practice slowing down, especially when explaining product features or pricing.`,
          'Use deliberate pauses after key points to give the customer time to process information.',
        ]
      : alertCount === 1
      ? ['One speech rate alert detected. Monitor pacing during complex or important moments.']
      : ['Great pacing throughout the call.'];

  const resultContent = (
    <div className="space-y-2">
      {alertCount === 0 ? (
        <p className="text-sm text-success font-medium">No speech rate alerts during this call.</p>
      ) : (
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-error">{alertCount}</p>
            <p className="text-[10px] text-ink-muted">alert{alertCount !== 1 ? 's' : ''}</p>
          </div>
          {avgWpm && (
            <div className="text-center">
              <p className="text-2xl font-bold text-warning">{avgWpm}</p>
              <p className="text-[10px] text-ink-muted">avg WPM</p>
            </div>
          )}
        </div>
      )}
      {speechRateAssessment && (
        <p className="text-xs text-ink-secondary mt-1">{speechRateAssessment}</p>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Speed"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingItems}
      sectionKey="speed"
      onViewInTimeline={alertCount > 0 ? onViewInTimeline : undefined}
    />
  );
};

export default SpeedSummaryCard;
